// ---------------------------------------------------------------------------
// Value resolution + formatting. Shared by the WYSIWYG canvas (display strings)
// and the Excel renderer (typed cell values). Must never throw on missing /
// nested / null data — a missing value resolves to blank.
// ---------------------------------------------------------------------------

import { getDerived, RECORD_LIST_PATH } from "../fieldCatalog";

/** Walk a dotted path (supports `a.b`, `a[0].b`). Returns undefined if absent. */
export function resolvePath(obj, path) {
  if (obj == null || !path) return undefined;
  return String(path)
    .split(".")
    .reduce((acc, key) => {
      if (acc == null) return undefined;
      const m = key.match(/^([^[]+)(?:\[(\d+)\])?$/);
      if (!m) return undefined;
      let v = acc[m[1]];
      if (m[2] != null && Array.isArray(v)) v = v[Number(m[2])];
      return v;
    }, obj);
}

/** Resolve a `collection[]` path to an array (empty if missing / not an array). */
export function resolveCollection(obj, collectionPath) {
  if (!collectionPath) return [];
  const v = resolvePath(obj, String(collectionPath).replace(/\[\]$/, ""));
  return Array.isArray(v) ? v : [];
}

const num = (v) => {
  const n = Number(v);
  return isNaN(n) ? null : n;
};

/** Parse both "yyyy-MM-dd HH:mm:ss" (space) and ISO "T" strings, epochs, Dates. */
export function toDate(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === "number") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  const s = String(v).trim();
  const d = new Date(s.includes("T") ? s : s.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function dateOnly(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Human-readable string for the canvas preview. */
export function formatValue(value, type) {
  if (value == null || value === "") return "";
  if (type === "currency") {
    const n = num(value);
    return n == null ? String(value) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (type === "number") {
    const n = num(value);
    return n == null ? String(value) : n.toLocaleString();
  }
  if (type === "date") {
    if (typeof value === "string") return value.split(/[ T]/)[0];
    const d = toDate(value);
    return d ? dateOnly(d) : String(value);
  }
  return String(value);
}

/** Typed cell value + Excel number format for ExcelJS. Never throws. */
export function coerceCell(value, type, opts = {}) {
  const currencyFormat = opts.currencyFormat || "#,##0.00";
  const dateFormat = opts.dateFormat || "yyyy-mm-dd";
  if (value == null || value === "") return { value: "" };
  switch (type) {
    case "number": {
      const n = num(value);
      return n == null ? { value: "" } : { value: n };
    }
    case "currency": {
      const n = num(value);
      return n == null ? { value: "" } : { value: n, numFmt: currencyFormat };
    }
    case "date": {
      const d = toDate(value);
      return d ? { value: d, numFmt: dateFormat } : { value: String(value) };
    }
    default:
      return { value: String(value) };
  }
}

/** Replace {{path}} tokens in a string with the record's values. */
export function interpolate(text, record) {
  return String(text == null ? "" : text).replace(/\{\{(.*?)\}\}/g, (_, p) => {
    const v = resolvePath(record, p.trim());
    if (v == null) return "";
    return formatValue(v, typeof v === "number" ? "number" : "string");
  });
}

/** Row filter for DATA_TABLE (e.g. lineItemType eq OUTPUT). */
export function passesFilter(el, rowFilter) {
  if (!rowFilter || !rowFilter.path) return true;
  const actual = resolvePath(el, rowFilter.path);
  const op = rowFilter.op || "eq";
  if (op === "ne") return String(actual) !== String(rowFilter.value);
  return String(actual) === String(rowFilter.value);
}

/** Compute a whitelisted derived value (no eval). */
export function computeDerived(record, def) {
  if (!def) return null;
  const left = num(resolvePath(record, def.left)) || 0;
  const right = num(resolvePath(record, def.right)) || 0;
  switch (def.op) {
    case "add":
      return left + right;
    case "subtract":
    default:
      return left - right;
  }
}

export function derivedByKey(record, source, key) {
  return computeDerived(record, getDerived(source)[key]);
}

/** Sum a numeric column path across a collection of rows. */
export function sumColumn(items, path) {
  return (items || []).reduce((acc, el) => {
    const n = Number(resolvePath(el, path));
    return acc + (isNaN(n) ? 0 : n);
  }, 0);
}

// ---------------------------------------------------------------------------
// Safe arithmetic formula evaluator (no eval). Supports + - * / , parentheses,
// numeric literals and field paths (resolved against the row). Used by computed
// columns, e.g. "cost - paidAmount" or "bill_qty * pricePerItem".
// ---------------------------------------------------------------------------

function tokenize(expr) {
  const re = /\s*([0-9]*\.?[0-9]+|[A-Za-z_][\w.[\]]*|[()+\-*/])/g;
  const tokens = [];
  let m;
  while ((m = re.exec(expr)) !== null) tokens.push(m[1]);
  return tokens;
}

export function evalFormula(expr, row) {
  if (!expr) return null;
  const tokens = tokenize(expr);
  if (!tokens.length) return null;
  let i = 0;
  const peek = () => tokens[i];
  const take = () => tokens[i++];

  const parseFactor = () => {
    const t = peek();
    if (t === "(") {
      take();
      const v = parseExpr();
      if (peek() === ")") take();
      return v;
    }
    if (t === "-") {
      take();
      return -parseFactor();
    }
    take();
    if (/^[0-9]*\.?[0-9]+$/.test(t)) return Number(t);
    const n = Number(resolvePath(row, t)); // field path -> numeric (0 if missing/non-numeric)
    return isNaN(n) ? 0 : n;
  };
  const parseTerm = () => {
    let v = parseFactor();
    while (peek() === "*" || peek() === "/") {
      const op = take();
      const r = parseFactor();
      v = op === "*" ? v * r : r !== 0 ? v / r : 0;
    }
    return v;
  };
  function parseExpr() {
    let v = parseTerm();
    while (peek() === "+" || peek() === "-") {
      const op = take();
      const r = parseTerm();
      v = op === "+" ? v + r : v - r;
    }
    return v;
  }

  const result = parseExpr();
  return typeof result === "number" && isFinite(result) ? result : null;
}

/** Raw value of a column for a row — computed formula or plain field path. */
export function columnRaw(row, col) {
  return col.computed ? evalFormula(col.computed, row) : resolvePath(row, col.path);
}

/** Sum a column (computed or path) across rows. */
export function sumColumnValues(items, col) {
  return (items || []).reduce((acc, el) => {
    const v = col.computed ? evalFormula(col.computed, el) : Number(resolvePath(el, col.path));
    return acc + (isFinite(v) ? Number(v) : 0);
  }, 0);
}

// ---------------------------------------------------------------------------
// Parent → child table rows. A table's grain (collectionPath) is the level it
// iterates: "" = one row per record; RECORD_LIST_PATH = one row per record in a
// list; a collection path = iterate that collection with the parent repeated.
// Each row is a { rec, el } pair — rec = the parent record, el = the element.
// ---------------------------------------------------------------------------

export function buildTableRows(record, sec, allRecords) {
  const grain = sec.collectionPath;
  if (!grain) return record ? [{ rec: record, el: record }] : [];
  if (grain === RECORD_LIST_PATH) {
    return (allRecords || [])
      .filter((r) => passesFilter(r, sec.rowFilter))
      .map((r) => ({ rec: r, el: r }));
  }
  if (!record) return [];
  return resolveCollection(record, grain)
    .filter((el) => passesFilter(el, sec.rowFilter))
    .map((el) => ({ rec: record, el }));
}

/**
 * Resolve one column against a { rec, el } row. PARENT columns read the parent
 * record (repeated on every child row); other columns read the element. Legacy
 * columns without a level read the element (unchanged behaviour). Computed
 * columns evaluate against parent + element merged.
 */
export function cellValue(rowPair, col) {
  if (!rowPair) return undefined;
  if (col.computed) return evalFormula(col.computed, { ...(rowPair.rec || {}), ...(rowPair.el || {}) });
  const base = col.level === "PARENT" ? rowPair.rec : rowPair.el;
  return resolvePath(base, col.path);
}

export function sumCells(rowPairs, col) {
  return (rowPairs || []).reduce((acc, rp) => {
    const v = Number(cellValue(rp, col));
    return acc + (isFinite(v) ? v : 0);
  }, 0);
}
